// Finances: what MyGaz earned over 7, 14 or 30 days against the same span
// before, where the money went (homes → riders' cash or Mobile Money →
// agencies → MyGaz), and every pawaPay payment. Reads ops_finance (0046).
// MyGaz takes no gas money: kits by Mobile Money, and the agencies'
// commission on each delivery fare.
import { useEffect, useMemo, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, dec1, discColor, fmtN, initials, niceScale, xaf } from '../lib/format';
import type { Source } from '../lib/api';
import type { Account, Finance, FinDay, Payment } from '../lib/types';
import { Ic } from '../components/Icon';
import { useToast } from '../components/Toast';
import type { Tab } from '../components/Shell';
import { Loading } from '../components/Skeleton';
import { ErrorState } from '../components/Busy';
import { useResource } from '../lib/useResource';

type P = 7 | 14 | 30;
type Node = 'hh' | 'cash' | 'momo' | 'ag' | 'keep' | 'mg';
const USD = 600; // XAF per USD, rounded: the label says ≈
const REDUCE = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const kXAF = (n: number) => (n >= 1e6 ? dec1(n / 1e6).replace(',0', '') + ' M' : fmtN(Math.round(n / 1000)) + ' k');
const usd = (n: number) => '≈ ' + fmtN(Math.round(n / USD / 10) * 10) + ' USD';

interface Sum { n: number; installs: number; gas: number; fares: number; kits: number; kitX: number; com: number; rev: number; moved: number }
function sum(ds: FinDay[]): Sum {
  const r = { n: 0, installs: 0, gas: 0, fares: 0, kits: 0, kitX: 0, com: 0 };
  ds.forEach((d) => { r.n += d.n; r.installs += d.installs; r.gas += d.gas; r.fares += d.fares; r.kits += d.kits; r.kitX += d.kit_xaf; r.com += d.com; });
  return { ...r, rev: r.com + r.kitX, moved: r.gas + r.fares + r.kitX };
}

/** Smooth monotone path through points (the prototype's monoPath). */
function monoPath(P: [number, number][]) {
  const n = P.length, dx: number[] = [], m: number[] = [], t: number[] = [];
  if (n < 2) return '';
  for (let i = 0; i < n - 1; i++) { dx[i] = P[i + 1][0] - P[i][0]; m[i] = (P[i + 1][1] - P[i][1]) / dx[i]; }
  t[0] = m[0]; t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (3 * (dx[i - 1] + dx[i])) / ((2 * dx[i] + dx[i - 1]) / m[i - 1] + (dx[i] + 2 * dx[i - 1]) / m[i]);
  let d = `M${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += `C${(P[i][0] + h).toFixed(1)} ${(P[i][1] + t[i] * h).toFixed(1)} ${(P[i + 1][0] - h).toFixed(1)} ${(P[i + 1][1] - t[i + 1] * h).toFixed(1)} ${P[i + 1][0].toFixed(1)} ${P[i + 1][1].toFixed(1)}`;
  }
  return d;
}

function useWidth(min = 300) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(min, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return [ref, w] as const;
}

interface Scrub { i: number; v: number; c: number; p: number; day: FinDay }

/** Cumulative revenue: kits on top of commissions, the span before dotted. */
function HeroChart({ days, prev, onScrub, h }: { days: FinDay[]; prev: number[]; onScrub(x: Scrub | null): void; h: number }) {
  const [ref, W0] = useWidth(280);
  const [hi, setHi] = useState<number | null>(null);
  const W = W0 || 700, H = h, n = days.length, pt = 24, pb = 26, pr = 10, ih = H - pt - pb, iw = W - pr;
  const cc: number[] = [], ca: number[] = [], pa: number[] = [];
  let c1 = 0, c2 = 0, c3 = 0;
  days.forEach((d, i) => { c1 += d.com; c2 += d.com + d.kit_xaf; c3 += prev[i] ?? 0; cc[i] = c1; ca[i] = c2; pa[i] = c3; });
  const sc = niceScale(Math.max(ca[n - 1] ?? 0, pa[n - 1] ?? 0) * 1.06, 3);
  const X = (i: number) => (i / Math.max(1, n - 1)) * iw, Y = (v: number) => pt + ih - (v / sc.mx) * ih, base = Y(0);
  const dT = monoPath(ca.map((v, i) => [X(i), Y(v)])), dC = monoPath(cc.map((v, i) => [X(i), Y(v)])), dP = monoPath(pa.map((v, i) => [X(i), Y(v)]));
  const lx = X(n - 1), ly = Y(ca[n - 1] ?? 0);
  const at = (e: React.PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const i = Math.max(0, Math.min(n - 1, Math.round((((e.clientX - r.left) * W) / r.width / iw) * (n - 1))));
    setHi(i); onScrub({ i, v: ca[i], c: cc[i], p: pa[i], day: days[i] });
  };
  return (
    <div className="chart" ref={ref}>
      {W0 > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={S.fin.chartAria(n)}>
          <defs>
            <linearGradient id="fzk" x1="0" x2="0" y1="0" y2="1"><stop offset="0" style={{ stopColor: 'var(--kit)', stopOpacity: 0.28 }} /><stop offset="1" style={{ stopColor: 'var(--kit)', stopOpacity: 0.03 }} /></linearGradient>
            <linearGradient id="fzc" x1="0" x2="0" y1="0" y2="1"><stop offset="0" style={{ stopColor: 'var(--blue)', stopOpacity: 0.4 }} /><stop offset="1" style={{ stopColor: 'var(--blue)', stopOpacity: 0.1 }} /></linearGradient>
            <clipPath id="fzr"><rect x="-10" y="0" width={W + 20} height={H}>{!REDUCE && <animate attributeName="width" from="0" to={W + 20} dur="1.1s" calcMode="spline" keySplines=".22 1 .36 1" keyTimes="0;1" fill="freeze" />}</rect></clipPath>
          </defs>
          {sc.ticks.slice(1).map((t) => (
            <g key={t}><line className="fz-gl" x1="0" x2={iw} y1={Y(t)} y2={Y(t)} /><text className="ax" x="0" y={Y(t) - 6}>{kXAF(t)}</text></g>
          ))}
          <line className="fz-base" x1="0" x2={iw} y1={base} y2={base} />
          <path d={dP} className="fz-prev" />
          <g clipPath="url(#fzr)">
            <path d={`${dT}L${lx} ${base}L0 ${base}Z`} fill="url(#fzk)" />
            <path d={`${dC}L${lx} ${base}L0 ${base}Z`} fill="url(#fzc)" />
            <path d={dC} className="fz-lc" /><path d={dT} className="fz-lt" />
          </g>
          <circle cx={lx} cy={ly} r="6" className="fz-pulse" /><circle cx={lx} cy={ly} r="5" className="fz-dot" />
          <text className="ax" x="0" y={H - 7}>{dayLabel(days[0].day + 'T12:00:00Z')}</text>
          <text className="ax" x={X(Math.round((n - 1) / 2))} y={H - 7} textAnchor="middle">{dayLabel(days[Math.round((n - 1) / 2)].day + 'T12:00:00Z')}</text>
          <text className="ax now" x={lx} y={H - 7} textAnchor="end">{S.fin.today}</text>
          {hi != null && <>
            <line className="fz-xh" x1={X(hi)} x2={X(hi)} y1={pt - 10} y2={base} />
            <circle className="fz-xp" r="4" cx={X(hi)} cy={Y(pa[hi])} />
            <circle className="fz-xd" r="5" cx={X(hi)} cy={Y(ca[hi])} />
          </>}
          <rect x="0" y="0" width={W} height={H} fill="transparent" style={{ touchAction: 'pan-y' }}
            onPointerMove={at} onPointerDown={at} onPointerLeave={() => { setHi(null); onScrub(null); }} />
        </svg>
      )}
    </div>
  );
}

interface FlowV { T: number; cash: number; kit: number; com: number }

/** Homes → cash / Mobile Money → agencies → kept / MyGaz, with moving dots sized to the money. */
function FlowChart({ v, sel, onSel, per }: { v: FlowV; sel: Node; onSel(n: Node): void; per: string }) {
  const [ref, W0] = useWidth(300);
  const pg = useRef<SVGGElement>(null);
  const W = W0 || 700, mob = W < 560, H = mob ? 268 : 330, lp = 38, pt = lp, V = H - 2 * lp, nw = mob ? 7 : 9;
  const T = Math.max(1, v.T), k = (V * 0.6) / T, cx = [0, (W - nw) / 3, ((W - nw) * 2) / 3, W - nw];
  const keepV = Math.max(0, v.cash - v.com), mgH = (v.com + v.kit) * k;
  const N: Record<Node, { c: number; y: number; h: number; a: number; top?: boolean; cl: string }> = {
    hh: { c: 0, y: pt + (V - v.T * k) / 2, h: v.T * k, a: v.T, top: true, cl: 'hh' },
    cash: { c: 1, y: pt, h: v.cash * k, a: v.cash, top: true, cl: 'nt' },
    momo: { c: 1, y: pt + V - v.kit * k, h: v.kit * k, a: v.kit, cl: 'kt' },
    ag: { c: 2, y: pt + V * 0.1, h: v.cash * k, a: v.cash, top: true, cl: 'nt' },
    keep: { c: 3, y: pt, h: keepV * k, a: keepV, top: true, cl: 'nt' },
    mg: { c: 3, y: pt + V - mgH, h: mgH, a: v.com + v.kit, cl: 'mg' },
  };
  const LK: [Node, Node, number, string, number, number, Node, string][] = [
    ['hh', 'cash', v.cash, 'cash', 0, 0, 'cash', S.fin.links.cash], ['hh', 'momo', v.kit, 'kt', v.cash * k, 0, 'momo', S.fin.links.kit],
    ['cash', 'ag', v.cash, 'cash', 0, 0, 'cash', S.fin.links.hand], ['ag', 'keep', keepV, 'cash', 0, 0, 'ag', S.fin.links.keep],
    ['ag', 'mg', v.com, 'com', keepV * k, 0, 'mg', S.fin.links.com], ['momo', 'mg', v.kit, 'kt', 0, v.com * k, 'mg', S.fin.links.kitMg],
  ];
  const links = LK.map(([a, b, val, kind, so, to, s2, tip]) => {
    const A = N[a], B = N[b], th = Math.max(2, val * k), x0 = cx[A.c] + nw, x1 = cx[B.c], y0 = A.y + so + th / 2, y1 = B.y + to + th / 2, xm = (x0 + x1) / 2;
    return { a, b, val, kind, sel: s2, tip, th, d: `M${x0} ${y0}C${xm} ${y0} ${xm} ${y1} ${x1} ${y1}` };
  });
  const rel = (id: Node) => links.map((l, i) => (l.a === id || l.b === id ? i : -1)).filter((i) => i >= 0);
  const on = rel(sel);
  const key = `${W}|${v.T}|${v.cash}|${v.kit}|${v.com}`;

  // The dots: imperative, one rAF loop, rebuilt when the sizes change.
  useEffect(() => {
    const g = pg.current;
    if (!g || REDUCE || !W0) return;
    const NS = 'http://www.w3.org/2000/svg', speed = mob ? 42 : 58;
    const paths = Array.from(g.parentElement!.querySelectorAll<SVGPathElement>('path.fl-l'));
    const L = paths.map((p) => ({ el: p, len: p.getTotalLength(), th: +p.getAttribute('stroke-width')!, kind: p.dataset.kind!, rate: Math.max(0.7, Math.sqrt(+p.dataset.v! / 1000) * 0.12), acc: 0 }));
    type Dot = { l: (typeof L)[number]; d: number; off: number; el: SVGCircleElement };
    let dots: Dot[] = [];
    const spawn = (l: (typeof L)[number], d = 0) => {
      if (+l.el.dataset.v! <= 0) return;
      const c = document.createElementNS(NS, 'circle');
      c.setAttribute('r', l.kind === 'cash' ? '1.7' : '2.1'); c.setAttribute('class', l.kind); g.appendChild(c);
      dots.push({ l, d, off: (Math.random() - 0.5) * Math.max(0, l.th - 5), el: c });
    };
    L.forEach((l) => { const n = Math.round((l.rate * l.len) / speed); for (let j = 0; j < n; j++) spawn(l, Math.random() * l.len); });
    let raf = 0, t0 = 0;
    const loop = (now: number) => {
      const dt = t0 ? Math.min(0.05, (now - t0) / 1000) : 0; t0 = now;
      L.forEach((l) => { l.acc += l.rate * dt; while (l.acc >= 1) { l.acc -= 1; spawn(l); } });
      dots = dots.filter((p) => {
        p.d += speed * dt;
        if (p.d >= p.l.len) { p.el.remove(); return false; }
        const a = p.l.el.getPointAtLength(p.d), b = p.l.el.getPointAtLength(Math.min(p.l.len, p.d + 1.5));
        const nx = a.y - b.y, ny = b.x - a.x, nl = Math.hypot(nx, ny) || 1;
        p.el.setAttribute('cx', (a.x + (nx / nl) * p.off).toFixed(1)); p.el.setAttribute('cy', (a.y + (ny / nl) * p.off).toFixed(1));
        p.el.setAttribute('opacity', Math.min(1, p.d / 14, (p.l.len - p.d) / 14).toFixed(2));
        return true;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); dots.forEach((p) => p.el.remove()); };
  }, [key, W0, mob]);

  return (
    <div className="chart" ref={ref}>
      {W0 > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={S.fin.flow} className={'fl' + (sel ? ' dim' : '')}>
          {links.map((l, i) => (
            <path key={i} className={'fl-l ' + l.kind + (on.includes(i) ? ' on' : '')} data-kind={l.kind} data-v={l.val} d={l.d} strokeWidth={l.th}
              onClick={() => onSel(l.sel)}><title>{`${l.tip} · ${per} · ${xaf(l.val)}`}</title></path>
          ))}
          <g className="fl-p" ref={pg} />
          {(Object.keys(N) as Node[]).map((id) => {
            const n = N[id], x = cx[n.c], al = n.c === 0 ? 'start' : n.c === 3 ? 'end' : 'middle', tx = n.c === 0 ? x : n.c === 3 ? x + nw : x + nw / 2, ty = n.top ? n.y - 22 : n.y + n.h + 17;
            const label = mob && id === 'keep' ? S.fin.nodes.ag : S.fin.nodes[id];
            return (
              <g key={id} className={'fl-n' + (sel === id ? ' on' : '')} tabIndex={0} role="button" aria-label={`${label}, ${fmtN(Math.round(n.a))} XAF`}
                onClick={() => onSel(id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSel(id); } }}>
                <rect x={x - 14} y={n.top ? n.y - 34 : n.y - 4} width={nw + 28} height={n.h + 38} fill="transparent" />
                {id === 'mg' ? <>
                  <rect x={x} y={n.y} width={nw} height={Math.max(2, v.com * k)} rx="2" className="fl-r com" />
                  <rect x={x} y={n.y + v.com * k} width={nw} height={Math.max(2, v.kit * k)} rx="2" className="fl-r kt" />
                </> : <rect x={x} y={n.y} width={nw} height={Math.max(2, n.h)} rx="2" className={'fl-r ' + n.cl} />}
                <text className="fl-t" x={tx} y={ty} textAnchor={al}>{label}</text>
                <text className="fl-a" x={tx} y={ty + 14} textAnchor={al}>{kXAF(n.a)}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

const acctState = (a: Account): [string, string] =>
  a.paused ? ['red', S.fin.paused] : a.balance < 0 ? ['red', S.fin.neg] : a.days_to_zero != null && a.days_to_zero < 3 ? ['amber', S.fin.low] : ['green', S.fin.good];

function AccountBox({ a }: { a: Account }) {
  const [c, l] = acctState(a);
  const wait = a.topups.find((t) => t.status === 'unknown' || t.status === 'accepted' || t.status === 'created');
  return (
    <div className="fz-acct">
      <div className="fz-ih"><span className="cap">{S.fin.account(a.name)}</span><span className={'st ' + c}>{l}</span></div>
      <div className="fz-in num">{a.balance < 0 ? '−' : ''}{fmtN(Math.abs(a.balance))}<small>XAF</small></div>
      <div className="fz-is">
        {a.balance <= 0 ? S.fin.atZero : a.days_to_zero != null ? S.fin.zeroIn(S.fin.inDays(a.days_to_zero)) : S.fin.noPace}
        {a.days_to_pause != null && <><br />{S.fin.pauseIn(S.fin.inDays(a.days_to_pause))}</>}
      </div>
      <div className="fz-kv">
        <div><span>{S.fin.feeDay}</span><b className="num">{S.fin.perDay(fmtN(a.fee_per_day))}</b></div>
        <div><span>{S.fin.refill30}</span><b className="num">≈ {fmtN(Math.round((a.fee_per_day * 30) / 1000) * 1000)}</b></div>
        <div><span>{S.fin.feePct}</span><b className="num">{a.fee_pct} %</b></div>
        <div><span>{S.fin.limit}</span><b className="num">−{fmtN(a.limit)}</b></div>
      </div>
      {wait && <div className="fz-warn"><span className="fz-st wait">{S.fin.noAnswer}</span><span>{S.fin.topupWait(fmtN(wait.amount))}</span></div>}
    </div>
  );
}

function Inspect({ id, f, cur, P, onFeed, openOrder }: { id: Node; f: Finance; cur: Sum; P: number; onFeed(): void; openOrder(id: string): void }) {
  if (id === 'mg') {
    const avgCom = cur.n ? cur.com / cur.n : 0, avgFare = cur.n ? cur.fares / cur.n : 1200, avgGas = cur.n ? cur.gas / cur.n : 6500;
    const pct = f.agencies[0]?.fee_pct ?? 20, comOne = Math.round((avgFare * pct) / 100 / 10) * 10;
    const [a, b, c] = S.fin.oneRefill(fmtN(Math.round((avgGas + avgFare) / 100) * 100), fmtN(comOne));
    return (<>
      <div className="fz-ih"><span className="cap">{S.fin.mgKeeps}</span></div>
      <div className="fz-in num">{fmtN(cur.rev)}<small>XAF</small></div>
      <div className="fz-is">{S.fin.ofMoved(cur.moved ? dec1((cur.rev / cur.moved) * 100) : '0')}</div>
      <div className="fz-split"><i className="kt" style={{ flex: cur.kitX || 0.0001 }} /><i className="com" style={{ flex: cur.com || 0.0001 }} /></div>
      <div className="fz-pair">
        <div><span><i className="sw kt" />{S.fin.kits}</span><b className="num">{fmtN(cur.kitX)}</b><em>{cur.kits} × {fmtN(cur.kits ? Math.round(cur.kitX / cur.kits) : 9000)}</em></div>
        <div><span><i className="sw com" />{S.fin.com}</span><b className="num">{fmtN(cur.com)}</b><em>{S.fin.courses(cur.n, fmtN(Math.round(avgCom)))}</em></div>
      </div>
      <div className="fz-one"><div className="fz-oneb"><i style={{ flex: avgGas }} /><i style={{ flex: avgFare - comOne }} /><i className="com" style={{ flex: comOne }} /></div>
        <span>{a}<b>{b}</b>{c}</span></div>
      {f.kits_pending.length > 0 && <>
        <div className="fz-chips">{f.kits_pending.slice(0, 4).map((k) => (
          <button key={k.order_id} className="fz-chip" onClick={() => openOrder(k.order_id)}><span className="ini" style={{ background: discColor(k.order_id) }}>{initials(k.customer)}</span>{k.customer.split(' ')[0]} · {k.ref}</button>
        ))}</div>
        <div className="fz-is" style={{ marginTop: 6 }}>{S.fin.pending}</div>
      </>}
      {f.refunds_due.n > 0 && <div className="fz-warn"><span className="fz-st no">{S.fin.refund}</span><span>{S.fin.refunds(f.refunds_due.n, xaf(f.refunds_due.amount))}</span></div>}
    </>);
  }
  if (id === 'ag' || id === 'keep') {
    if (!f.agencies.length) return <div className="fz-is">{S.fin.noTopup}</div>;
    return <>{f.agencies.map((a) => <AccountBox key={a.agency_id} a={a} />)}</>;
  }
  if (id === 'cash') {
    const t = f.cash_today.reduce((s, x) => s + x.amount, 0), top = f.cash_today[0]?.amount || 1;
    return (<>
      <div className="fz-ih"><span className="cap">{S.fin.cashToday}</span></div>
      <div className="fz-in num">{fmtN(t)}<small>XAF</small></div>
      <div className="fz-is">{f.cash_today.length ? S.fin.cashSub(f.cash_today.length) : S.fin.noCash}</div>
      <div className="fz-riders">{f.cash_today.map((x) => (
        <div key={x.rider_id}><span className="ini" style={{ background: discColor(x.rider_id) }}>{initials(x.name)}</span><span className="nm">{x.name.split(' ')[0]}</span>
          <span className="rb"><i style={{ width: (x.amount / top) * 100 + '%' }} /></span><b className="num">{fmtN(x.amount)}</b></div>
      ))}</div>
      {f.cash_on_way.n > 0 && <div className="fz-is" style={{ marginTop: 12 }}><Ic n="moto" s="xs" /> {S.fin.onWay(f.cash_on_way.n, fmtN(f.cash_on_way.amount))}</div>}
    </>);
  }
  if (id === 'momo') {
    const ps = f.payments, ok = ps.filter((p) => p.status === 'completed'), dec = ps.filter((p) => ['completed', 'failed', 'rejected'].includes(p.status));
    const secs = ok.map((p) => p.secs).filter((s): s is number => s != null), mtn = ps.filter((p) => p.provider === 'MTN_MOMO_CMR').length;
    const wait = ps.find((p) => p.status === 'unknown'), fail = ps.find((p) => p.status === 'failed');
    return (<>
      <div className="fz-ih"><span className="cap">{S.fin.momo}</span></div>
      <div className="fz-in num">{ok.length}<small>{S.fin.received(dec.length)}</small></div>
      <div className="fz-is">{S.fin.avgSecs(secs.length ? String(Math.round(secs.reduce((a, b) => a + b, 0) / secs.length)) : '—')}</div>
      <div className="fz-split" style={{ marginTop: 16 }}><i className="mtn" style={{ flex: mtn || 0.0001 }} /><i className="org" style={{ flex: ps.length - mtn || 0.0001 }} /></div>
      <div className="fz-lg"><span><i className="sw mtn" />MTN {mtn}</span><span><i className="sw org" />Orange {ps.length - mtn}</span></div>
      {wait && <div className="fz-warn"><span className="fz-st wait">{S.fin.noAnswer}</span><span>{(wait.kind === 'kit' ? S.fin.kit : S.fin.topup) + ' · ' + (S.fin.prov[wait.provider] ?? '') + ' ' + fmtN(wait.amount)}</span></div>}
      {fail && <div className="fz-warn"><span className="fz-st no">{S.fin.failedP}</span><span>{S.fin.failedSub(fail.who.split(' ')[0])}</span></div>}
      <button className="link fz-act" onClick={onFeed}>{S.fin.allPay} <Ic n="chevd" s="xs" /></button>
    </>);
  }
  return (<>
    <div className="fz-ih"><span className="cap">{S.fin.hhTitle}</span></div>
    <div className="fz-in num">{fmtN(cur.moved)}<small>XAF</small></div>
    <div className="fz-is">{S.fin.deliveries(cur.n)} · {S.fin.installs(cur.installs)} · {P} j</div>
    <div className="fz-kv">
      <div><span>{S.fin.gas}</span><b className="num">{fmtN(cur.gas)}</b></div>
      <div><span>{S.fin.fares}</span><b className="num">{fmtN(cur.fares)}</b></div>
      <div><span>{S.fin.kitsPaid}</span><b className="num">{fmtN(cur.kitX)}</b></div>
    </div>
  </>);
}

function when(iso: string) {
  const d = new Date(iso), k = (x: Date) => x.toLocaleDateString('fr-FR', { timeZone: 'Africa/Douala' });
  const t = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Douala' });
  if (k(d) === k(new Date())) return S.fin.todayW + ' ' + t;
  if (k(d) === k(new Date(Date.now() - 86_400_000))) return S.fin.yday + ' ' + t;
  return dayLabel(d, { weekday: 'short', day: 'numeric', month: 'short' });
}

function PayRow({ p }: { p: Payment }) {
  const kit = p.kind === 'kit', st = p.status;
  const cls = st === 'completed' ? 'ok' : st === 'failed' || st === 'rejected' ? 'no' : 'wait';
  return (
    <div className="fz-tx">
      <span className={'fz-ic ' + (kit ? 'k' : 'a')}><Ic n={kit ? 'box' : 'wallet'} s="s" /></span>
      <div className="m"><b>{kit ? S.fin.kit : S.fin.topup} · {p.who}</b>
        <span>{S.fin.prov[p.provider] ?? p.provider} · {when(p.created_at)}{p.ref ? ' · ' + p.ref : ''}{p.failure_code && cls === 'no' ? ' · ' + p.failure_code.toLowerCase().replace(/_/g, ' ') : ''}{p.env === 'sandbox' ? ' · ' + S.fin.test : ''}</span></div>
      <b className="num am">{fmtN(p.amount)} XAF</b>
      <span className="s"><span className={'fz-st ' + cls}>{S.fin.st[st] ?? st}</span>{p.refund_due && <span className="fz-st no">{S.fin.refund}</span>}</span>
    </div>
  );
}

function csv(f: Finance) {
  const L = [S.fin.csv.head, ...f.days.map((d) => [d.day, d.n, d.installs, d.gas, d.fares, d.kits, d.kit_xaf, d.com].join(';')), '', S.fin.csv.pay,
    ...f.payments.map((p) => [p.created_at, p.kind, `"${p.who.replace(/"/g, '""')}"`, p.provider, p.amount, p.status, p.env].join(';'))];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + L.join('\n')], { type: 'text/csv;charset=utf-8' }));
  a.download = `mygaz-finances-${f.period}j-${f.days[f.days.length - 1].day}.csv`;
  a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

interface Props { src: Source; refresh: Date | null; go(t: Tab): void; openOrder(id: string): void; focus?: string | null }

export function Finances({ src, refresh, openOrder, focus }: Props) {
  const [P, setP] = useState<P>(30);
  // A new period keeps the last figures on screen, dimmed, until the new ones arrive.
  const r = useResource<Finance>(() => src.finance(P), [src, P], { refresh: [refresh], keep: true });
  const f = r.data;
  const [sel, setSel] = useState<Node>(focus === 'ag' ? 'ag' : 'mg');
  const [scrub, setScrub] = useState<Scrub | null>(null);
  const [more, setMore] = useState(false);
  const toast = useToast();
  const feed = useRef<HTMLElement>(null);
  const ins = useRef<HTMLDivElement>(null);

  const calc = useMemo(() => {
    if (!f) return null;
    const half = f.days.length / 2, curD = f.days.slice(half), prevD = f.days.slice(0, half);
    const cur = sum(curD), prev = sum(prevD), last14 = sum(f.days.slice(-14));
    return { curD, prevD, cur, prev, pace: last14.rev / 14, dl: prev.rev ? Math.round(((cur.rev - prev.rev) / prev.rev) * 100) : 0 };
  }, [f]);

  if (r.status === 'error') return <ErrorState error={r.error!} onRetry={r.reload} retrying={r.retrying} />;
  if (!f || !calc) return <Loading page="fin" />;
  const { cur, prev, curD, prevD, dl, pace } = calc;
  const pick = (n: Node) => { setSel(n); if (innerWidth <= 1100) setTimeout(() => ins.current?.scrollIntoView({ block: 'nearest', behavior: REDUCE ? 'auto' : 'smooth' }), 30); };
  const g = scrub ? (scrub.p ? Math.round(((scrub.v - scrub.p) / scrub.p) * 100) : 0) : dl;
  const alerts = f.agencies.filter((a) => a.paused || a.balance < 0 || (a.days_to_zero != null && a.days_to_zero < 3));
  const pays = more ? f.payments : f.payments.slice(0, 5);

  return (
    <section className={'view on' + (r.stale ? ' stale' : '')} aria-label={S.fin.title} aria-busy={r.stale}>
      <div className="ph"><div><h1>{S.fin.title}</h1><p>{S.fin.sub}</p></div>
        <div className="r">
          <div className="seg" role="group" aria-label={S.fin.period}>
            {([7, 14, 30] as P[]).map((v) => <button key={v} aria-pressed={v === P} onClick={() => { setP(v); setScrub(null); }}>{S.fin.days(v)}</button>)}
          </div>
          <button className="btn fz-ib" aria-label={S.fin.export} title={S.fin.export} onClick={() => { csv(f); toast(S.fin.exported); }}><Ic n="download" s="s" /></button>
        </div>
      </div>

      <section className="card fz-hero">
        <div className="fz-top">
          <div className="fz-head">
            <span className="cap">{scrub ? (scrub.i === curD.length - 1 ? S.fin.cumToday : S.fin.cumTo(dayLabel(scrub.day.day + 'T12:00:00Z', { weekday: 'short', day: 'numeric', month: 'short' }))) : S.fin.rev(P)}</span>
            <div className="fz-num"><span className="num">{fmtN(scrub ? scrub.v : cur.rev)}</span><small>XAF</small><span className="fz-usd">{usd(scrub ? scrub.v : cur.rev)}</span></div>
            <div className="fz-sub">
              <span className={'delta' + (g < 0 ? ' dn' : g === 0 ? ' flat' : '')}><Ic n={g < 0 ? 'chevd' : 'up'} s="xs" />{g > 0 ? '+' : ''}{g} %</span>
              {scrub ? S.fin.dayAdd(fmtN(scrub.day.com + scrub.day.kit_xaf), scrub.day.n, scrub.day.kits) : S.fin.vsPrev(P)}
            </div>
          </div>
          <div className="fz-leg">
            <div><span><i className="sw kt" />{S.fin.kits}</span><b className="num">{fmtN(scrub ? scrub.v - scrub.c : cur.kitX)}</b></div>
            <div><span><i className="sw com" />{S.fin.com}</span><b className="num">{fmtN(scrub ? scrub.c : cur.com)}</b></div>
            <div><span><i className="sw dash" />{S.fin.prev(P)}</span><b className="num fz-pv">{fmtN(scrub ? scrub.p : prev.rev)}</b></div>
            <div className="fz-pj"><span><Ic n="arrow" s="xs" />{S.fin.proj(P)}</span><b className="num">≈ {fmtN(Math.round((pace * P) / 1000) * 1000)}</b></div>
          </div>
        </div>
        <HeroChart key={P} days={curD} prev={prevD.map((d) => d.com + d.kit_xaf)} onScrub={setScrub} h={typeof innerWidth !== 'undefined' && innerWidth > 860 ? 236 : 190} />
      </section>

      {alerts.map((a) => {
        const wait = a.topups.find((t) => t.status === 'unknown');
        const [nm, rest] = a.paused ? S.fin.alertPaused(a.name) : S.fin.alert(a.name, S.fin.inDays(a.days_to_zero ?? 0));
        return (
          <div key={a.agency_id} className="fz-alert"><span className="fz-pd" />
            <button className="fz-at" onClick={() => pick('ag')}><b>{nm}</b>{rest}{wait ? S.fin.alertWait(fmtN(wait.amount)) : ''}</button>
            <button className="btn sm" onClick={() => pick('ag')}>{S.fin.see}</button>
          </div>
        );
      })}

      <section className="card fz-flow">
        <div className="fz-fl"><div className="fz-fh"><h2>{S.fin.flow}</h2><span>{S.fin.flowSub(P)}</span></div>
          <FlowChart v={{ T: cur.moved, cash: cur.gas + cur.fares, kit: cur.kitX, com: cur.com }} sel={sel} onSel={pick} per={S.fin.rev(P)} /></div>
        <div className="fz-ins" ref={ins}><div key={sel}><Inspect id={sel} f={f} cur={cur} P={P} openOrder={openOrder} onFeed={() => feed.current?.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' })} /></div></div>
      </section>

      <section className="card fz-feed" ref={feed}>
        <div className="ch"><div><h2>{S.fin.feed}</h2><div className="sub">{S.fin.feedSub}</div></div></div>
        <div className="fz-rows">{pays.length ? pays.map((p) => <PayRow key={p.id} p={p} />) : <div className="empty"><b>{S.fin.noPay}</b></div>}</div>
        {f.payments.length > 5 && <button className="fz-more" onClick={() => setMore((m) => !m)}>{more ? S.fin.less : S.fin.more(f.payments.length - 5)}<Ic n={more ? 'up' : 'chevd'} s="xs" /></button>}
      </section>
    </section>
  );
}
